import classNames from 'classnames'
import React, { useMemo, useState } from 'react'

import { type ExamKeyword, type SearchedExam } from '../api/interfaces/sconnStudyData'
import { useLearningKeywords } from '../module/sconnStudyData/hook'
import { useSurvey } from '../module/survey/hook'
import { type SelectedExamItem } from '../module/survey/types'
import { useInfoToast } from '../util/hooks/useToast'

import LoadingFallback from './common/LoadingFallback'
import ExamSearchModal from './ExamSearchModal'
import { SearchIcon } from './icons/icon'
import FormContent from './surveyFormContentLayout'

export const MAX_SELECTED_EXAM_COUNT = 4

const OTHERS_CATEGORY_ID = 'others'
const ICON_BASE = `${process.env.PUBLIC_URL}/img/learning-keyword`
// API iconUrl이 비어있을 때 사용하는 대분류 아이콘 (참고 프로젝트 자산 이식)
const CATEGORY_ICON_BY_ID: Record<string, string> = {
  '1': `${ICON_BASE}/ic_public_servant.svg`,
  '2': `${ICON_BASE}/ic_teacher.svg`,
  '3': `${ICON_BASE}/ic_CSAT.svg`,
  '4': `${ICON_BASE}/ic_colleage.svg`,
  '5': `${ICON_BASE}/ic_medical_license.svg`,
  '6': `${ICON_BASE}/ic_professional.svg`,
  '7': `${ICON_BASE}/ic_toeic.svg`,
  '8': `${ICON_BASE}/ic_computer.svg`,
  '9': `${ICON_BASE}/ic_history.svg`,
  '10': `${ICON_BASE}/ic_school.svg`,
  [OTHERS_CATEGORY_ID]: `${ICON_BASE}/ic_other.svg`,
}

type ExamCategory = {
  id: string
  name: string
  iconSrc?: string
  children: ExamKeyword[]
}

function CategoryCard({
  category,
  isFocused,
  selectedCount,
  onClick,
}: {
  category: ExamCategory
  isFocused: boolean
  selectedCount: number
  onClick: () => void
}) {
  const isActive = isFocused || selectedCount > 0

  return (
    <button
      type='button'
      onClick={onClick}
      aria-pressed={isActive}
      className={classNames(
        'relative flex min-h-[106px] flex-col items-center justify-center gap-[8px] rounded-[12px] border bg-white p-[12px] text-center',
        isFocused
          ? 'border-button-primary-blue shadow-[0_4px_0_0_#676ED3]'
          : isActive
            ? 'border-button-primary-blue'
            : 'border-common-line',
      )}
    >
      {category.iconSrc && (
        <img src={category.iconSrc} alt='' aria-hidden='true' className='h-[40px] w-[40px]' />
      )}
      <span
        className={classNames(
          'text-[13px]',
          isActive ? 'text-text-blue-primary' : 'text-text-black-primary',
        )}
      >
        {category.name}
      </span>
      {selectedCount > 0 && (
        <span className='absolute -right-[3px] -top-[3px] flex h-[24px] min-w-[42px] items-center justify-center rounded-full bg-button-primary-blue px-[8px] text-[13px] text-white'>
          +{selectedCount}
        </span>
      )}
    </button>
  )
}

function KeywordChip({
  label,
  isSelected,
  isDisabled,
  onClick,
  onDisabledClick,
}: {
  label: string
  isSelected: boolean
  isDisabled: boolean
  onClick: () => void
  onDisabledClick: () => void
}) {
  const handleClick = () => {
    if (isDisabled && !isSelected) {
      onDisabledClick()
      return
    }

    onClick()
  }

  return (
    <button
      type='button'
      onClick={handleClick}
      aria-disabled={isDisabled && !isSelected}
      className={classNames(
        'flex h-[32px] items-center gap-[4px] rounded-full border px-[12px] text-[13px]',
        isSelected
          ? 'border-button-primary-blue bg-white text-text-blue-primary'
          : isDisabled
            ? 'cursor-not-allowed border-common-line bg-surface-bg text-text-gray-unable'
            : 'border-common-line bg-white text-text-black-secondary',
      )}
    >
      {isSelected ? (
        <span
          aria-hidden='true'
          className='flex h-[16px] w-[16px] items-center justify-center rounded-full bg-button-primary-blue text-[10px] text-white'
        >
          ✕
        </span>
      ) : (
        <span aria-hidden='true' className='text-[14px]'>
          +
        </span>
      )}
      {label}
    </button>
  )
}

function SelectedExamChip({ label, onRemove }: { label: string; onRemove: () => void }) {
  return (
    <button
      type='button'
      onClick={onRemove}
      className='flex h-[40px] items-center gap-[6px] rounded-[22px] bg-writingview-timerbar px-[16px] text-[13px] font-medium text-text-blue-primary'
    >
      <span
        aria-hidden='true'
        className='flex h-[16px] w-[16px] items-center justify-center rounded-full bg-button-primary-blue text-[10px] text-white'
      >
        ✕
      </span>
      {label}
    </button>
  )
}

// V2 시험 선택 step: 학습키워드 트리(대분류 카드 → 하위 키워드 칩) + 시험 검색/직접 입력
export default function ExamKeywordSelect() {
  const { data, isPending, isFetching } = useLearningKeywords()
  const { selectedExamItems, setSelectedExamItems, surveyOnModal } = useSurvey()
  const { setOpenToast, setMessage, renderToast } = useInfoToast()
  const [focusedCategoryId, setFocusedCategoryId] = useState<string | null>(null)
  const [openSearchModal, setOpenSearchModal] = useState<boolean>(false)
  const isLoadingLearningKeywords = isPending || (isFetching && data === undefined)

  const categories = useMemo<ExamCategory[]>(() => {
    const keywords = data ?? []

    if (keywords.length === 0) {
      return []
    }

    const withChildren: ExamCategory[] = keywords
      .filter((keyword) => keyword.children.length > 0)
      .map((keyword) => ({
        id: `${keyword.id}`,
        name: keyword.name,
        iconSrc: keyword.iconUrl ?? CATEGORY_ICON_BY_ID[`${keyword.id}`],
        children: keyword.children,
      }))

    // children이 없는 대분류들은 '다른 관심사가 있어요' 카드의 하위 항목으로 묶는다
    const others: ExamKeyword[] = keywords
      .filter((keyword) => keyword.children.length === 0)
      .map((keyword) => ({ id: keyword.id, name: keyword.name }))

    return [
      ...withChildren,
      {
        id: OTHERS_CATEGORY_ID,
        name: '다른 관심사가 있어요',
        iconSrc: CATEGORY_ICON_BY_ID[OTHERS_CATEGORY_ID],
        children: others,
      },
    ]
  }, [data])

  const categoryRows = useMemo(() => {
    return categories.reduce<ExamCategory[][]>((rows, category, index) => {
      if (index % 3 === 0) {
        rows.push([category])
      } else {
        rows[rows.length - 1].push(category)
      }
      return rows
    }, [])
  }, [categories])

  const selectedIdSet = useMemo(
    () => new Set(selectedExamItems.map((item) => item.id)),
    [selectedExamItems],
  )
  const isLimitReached = selectedExamItems.length >= MAX_SELECTED_EXAM_COUNT

  const showLimitToast = () => {
    setMessage('최대 4개까지 선택할 수 있어요.')
    setOpenToast(true)
  }

  const toggleExamItem = (item: SelectedExamItem) => {
    if (selectedIdSet.has(item.id)) {
      setSelectedExamItems(selectedExamItems.filter((selected) => selected.id !== item.id))
      return
    }

    if (isLimitReached) {
      showLimitToast()
      return
    }

    setSelectedExamItems([...selectedExamItems, item])
  }

  const handleKeywordChipClick = (keyword: ExamKeyword, categoryId: string) => {
    toggleExamItem({
      id: `keyword-${keyword.id}`,
      label: keyword.name,
      source: 'keyword',
      categoryId,
      keywordId: keyword.id,
    })
  }

  const handleSelectSearchedExam = (exam: SearchedExam) => {
    toggleExamItem({
      id: `search-${exam.examId}`,
      label: exam.examName,
      source: 'search',
    })
  }

  const handleAddCustomExam = (label: string) => {
    const normalizedLabel = label.trim()

    if (!normalizedLabel) {
      return
    }

    const id = `custom-${normalizedLabel.toLowerCase()}`

    if (!selectedIdSet.has(id)) {
      if (isLimitReached) {
        showLimitToast()
        return
      }

      setSelectedExamItems([
        ...selectedExamItems,
        { id, label: normalizedLabel, source: 'custom' },
      ])
    }

    setOpenSearchModal(false)
  }

  if (isLoadingLearningKeywords) {
    return (
      <FormContent>
        <FormContent.Grow className='mb-10'>
          {!surveyOnModal && (
            <FormContent.Question
              title='준비 중인 시험을 알려주세요'
              description='학습 키워드를 불러오고 있어요.'
            />
          )}
          <LoadingFallback />
        </FormContent.Grow>
        <FormContent.ProcessButton disabled onNextClick={() => {}} />
      </FormContent>
    )
  }

  return (
    <FormContent>
      <FormContent.Grow className='mb-10'>
        {!surveyOnModal && (
          <FormContent.Question
            title='준비 중인 시험을 알려주세요'
            description={`최대 4개까지 선택할 수 있어요. (${selectedExamItems.length}/${MAX_SELECTED_EXAM_COUNT})`}
          />
        )}

        <div
          className={classNames(
            'flex flex-col gap-[8px]',
            surveyOnModal ? 'mt-[10px]' : 'mt-[30px]',
          )}
        >
          {categoryRows.map((row) => {
            const focusedCategory = row.find((category) => category.id === focusedCategoryId)

            return (
              <div key={row.map((category) => category.id).join('-')} className='flex flex-col gap-[4px]'>
                <div
                  className={classNames(
                    'grid gap-[8px]',
                    row.length === 2 ? 'grid-cols-2' : 'grid-cols-3',
                  )}
                >
                  {row.map((category) => {
                    const selectedCount = selectedExamItems.filter(
                      (item) => item.categoryId === category.id,
                    ).length

                    return (
                      <CategoryCard
                        key={category.id}
                        category={category}
                        isFocused={focusedCategoryId === category.id}
                        selectedCount={selectedCount}
                        onClick={() => {
                          setFocusedCategoryId((previousId) =>
                            previousId === category.id ? null : category.id,
                          )
                        }}
                      />
                    )
                  })}
                </div>

                {focusedCategory && (
                  <div className='max-h-[128px] w-full overflow-y-auto rounded-[12px] border border-dashed border-button-secondary-blue bg-white'>
                    <div className='flex flex-wrap gap-[8px] p-[12px]'>
                      {focusedCategory.children.map((keyword) => {
                        const isSelected = selectedIdSet.has(`keyword-${keyword.id}`)

                        return (
                          <KeywordChip
                            key={keyword.id}
                            label={keyword.name}
                            isSelected={isSelected}
                            isDisabled={!isSelected && isLimitReached}
                            onClick={() => {
                              handleKeywordChipClick(keyword, focusedCategory.id)
                            }}
                            onDisabledClick={showLimitToast}
                          />
                        )
                      })}
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>

        <button
          type='button'
          onClick={() => {
            setOpenSearchModal(true)
          }}
          className='mt-[20px] flex h-[40px] w-full items-center rounded-[40px] border border-common-line bg-white px-[16px] text-left text-[14px] text-sub-font'
        >
          <span className='flex-1'>준비 중인 시험 찾기</span>
          <SearchIcon stroke='black' />
        </button>

        {selectedExamItems.length > 0 && (
          <div className='mt-[15px] flex max-h-[96px] flex-wrap gap-[8px] overflow-y-auto'>
            {selectedExamItems.map((item) => (
              <SelectedExamChip
                key={item.id}
                label={item.label}
                onRemove={() => {
                  toggleExamItem(item)
                }}
              />
            ))}
          </div>
        )}

        <ExamSearchModal
          open={openSearchModal}
          isSelectionLimitReached={isLimitReached}
          isExamSelected={(exam) => selectedIdSet.has(`search-${exam.examId}`)}
          onLimitReached={showLimitToast}
          onSelectExam={handleSelectSearchedExam}
          onAddCustomExam={handleAddCustomExam}
          onClose={() => {
            setOpenSearchModal(false)
          }}
        />
        {renderToast()}
      </FormContent.Grow>
      <FormContent.ProcessButton
        disabled={selectedExamItems.length === 0}
        onNextClick={() => {}}
      />
    </FormContent>
  )
}
